import { useCallback, useMemo } from 'react'
import { PlotRange } from 'components/cell-visualizations/tsv/types'
import { plotlySlice } from '../PlotlySlice'
import useSliceWrapper from './useSliceWrapper'

const useGetMethodsAndData = () => {
  const methodsAndData = useSliceWrapper('plotly', plotlySlice)
  const { setSinglePlotRange, setMultiPlotRange } = methodsAndData

  // useMemo is used here to ensure that the helper methods are stable and do not trigger unnecessary re-renders
  const helperMethods = useMemo(() => {
    return {
      resetRange: (isMultiPlotMode?: boolean) => {
        if (isMultiPlotMode === undefined) {
          setSinglePlotRange({ x1: undefined, x2: undefined, y1: undefined, y2: undefined })
          setMultiPlotRange({ x1: undefined, x2: undefined, y1: undefined, y2: undefined })
        } else if (isMultiPlotMode) {
          setMultiPlotRange({ x1: undefined, x2: undefined, y1: undefined, y2: undefined })
        } else {
          setSinglePlotRange({ x1: undefined, x2: undefined, y1: undefined, y2: undefined })
        }
      }
    }
  }, [setSinglePlotRange, setMultiPlotRange])

  return { ...methodsAndData, ...helperMethods }
}

export const usePlotlySlice = (): ReturnType<typeof useGetMethodsAndData> => useGetMethodsAndData()

/**
 * Hook to get mode-specific range and setter
 */
export const usePlotRange = (isMultiPlotMode: boolean): { range: PlotRange; setRange: (newRange: PlotRange) => void } => {
  const { plotly, setSinglePlotRange, setMultiPlotRange } = usePlotlySlice()
  const { singlePlotRange, multiPlotRange } = plotly

  const range = isMultiPlotMode ? multiPlotRange : singlePlotRange

  const setRange = useCallback((newRange: PlotRange) => {
    if (isMultiPlotMode) {
      setMultiPlotRange(newRange)
    } else {
      setSinglePlotRange(newRange)
    }
  }, [isMultiPlotMode, setSinglePlotRange, setMultiPlotRange])

  return { range, setRange }
}

export default usePlotlySlice
